<template>
  <div
    class="c-featured-products"
    :class="featuredProductsClass"
  >
    <div class="l-container">
      <h2 :class="titleClass">
        {{ title }}
      </h2>
      <p
        v-if="subtitle"
        :class="subtitleClass"
      >
        {{ subtitle }}
      </p>
      <div
        v-if="widthFilter"
        class="c-featured-products__filter l-flex"
      >
        <template v-for="ringWidth in ringWidths">
          <button
            :key="ringWidth"
            class="c-featured-products__width u-margin-bottom-x-large"
            tabindex="0"
            @click="width = ringWidth"
          >
            <ring-width
              :width="ringWidth"
              :active="width"
            />
          </button>
        </template>
      </div>
      <div
        v-if="productCard && filteredProducts"
        class="l-grid l-grid--2-col-fixed"
      >
        <template v-for="(product, index) in filteredProducts">
          <product-card
            :key="product.title + index"
            :product="product"
          />
        </template>
      </div>
      <product-carousel
        v-else-if="filteredProducts"
        :products="filteredProducts"
        :classes="carouselClass"
        :center-carousel="centerCarousel"
      />
      <btn
        v-if="cta"
        v-bind="cta"
      >
        {{ cta.text }}
      </btn>
    </div>
  </div>
</template>

<script>
import Btn from "@/components/button/Btn";
import ProductCarousel from "../carousel/ProductCarousel";
import ProductCard from "@/components/card/ProductCard.vue";
import RingWidth from "@/components/ringWidth/RingWidth";
import isMobile from '@mixins/isMobile';

/**
   * wrap the stateless HomepageFeaturedRings component in a stateful context
   */
export default {
  components: {
    Btn,
    ProductCarousel,
    ProductCard,
    RingWidth
  },
  mixins: [
    //this.isMobile is true screen width < 900px
    isMobile
  ],
  props: {
    productCard: {
      type: Boolean,
      default: false
    },
    textAlignment: {
      type: String,
      default: "center"
    },
    mobileTextAlignment: {
      type: String,
      default: "center"
    },
    title: {
      type: String,
      default: ""
    },
    titleTypography: {
      type: String,
      default: "t-heading-3"
    },
    titleMarginBottom: {
      type: String,
      default: "x-small"
    },
    titleColor: {
      type: String,
      default: ""
    },
    subtitle: {
      type: String,
      default: ""
    },
    subtitleTypography: {
      type: String,
      default: "t-paragraph"
    },
    subtitleMarginBottom: {
      type: String,
      default: "medium"
    },
    subtitleColor: {
      type: String,
      default: ""
    },
    products: {
      type: Array,
      default: () => []
    },
    centerCarousel: {
      type: Boolean,
      default: false
    },
    carouselButtonColor: {
      type: String,
      default: ''
    },
    widthFilter: {
      type: Boolean,
      default: false
    },
    cta: {
      type: Object,
      default: () => {}
    },
    backgroundColor: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      width: 'Standard',
      widthOrder: [
        'Wide',
        'Standard',
        'Thin',
        'Halo',
        'Stackable',
        'Accent',
      ]
    }
  },
  computed: {
    ringWidths() {
      if (this.widthFilter) {
        let widths = [];
        for (const product of this.products) {
          let width = this.getProductWidth(product)
          if (!widths.includes(width)) {
            widths.push(width)
          }
        }
        widths.sort((a,b)=>{
          return this.widthOrder.indexOf(a) - this.widthOrder.indexOf(b)
        })
        return widths;
      }
      return []
    },
    filteredProducts() {
      if (this.widthFilter) {
        let tempProducts = [];
        for (let i = 0; i < this.products.length; i++) {
          let curWidth = this.getProductWidth(this.products[i])
          if (curWidth) {
            if (curWidth === this.width ) {
              tempProducts.push(this.products[i]);
            }
          }
        }
        return tempProducts;
      }
      return this.products;
    },
    featuredProductsClass() {
      let results = []
      if (this.backgroundColor) {
        results.push(" u-background-color-" + this.backgroundColor)
      }
      return results
    },
    carouselClass() {
      let results = []
      if (this.cta) {
        results.push('u-margin-bottom-x-large');
      }
      if (this.carouselButtonColor) {
        results.push("c-featured-products__carousel--button-" + this.carouselButtonColor)
      }

      return results.join(' ')
    },
    titleClass() {
      let results = ['c-featured-products__title']
      results.push(this.titleTypography);
      if (this.titleColor) {
        results.push(this.titleTypography + '--' + this.titleColor);
      }
      results.push('u-margin-bottom-' + this.titleMarginBottom);
      if (this.isMobile) {
        if (this.mobileTextAlignment) {
          results.push("u-text-align-" + this.mobileTextAlignment)
        }
      } else {
        results.push("u-text-align-" + this.textAlignment)
      }
      return results;
    },
    subtitleClass() {
      let results = ['c-featured-products__subtitle']
      results.push(this.subtitleTypography);
      if (this.subtitleColor) {
        results.push(this.subtitleTypography + '--' + this.subtitleColor);
      }
      results.push('u-margin-bottom-' + this.subtitleMarginBottom);
      if (this.isMobile) {
        if (this.mobileTextAlignment) {
          results.push("u-text-align-" + this.mobileTextAlignment)
        }
      } else {
        results.push("u-text-align-" + this.textAlignment)
      }
      return results;
    }
  },
  methods: {
    getProductWidth(product){
      return product.metafields.find(obj => obj.key === "width").value;
    }
  }
}
</script>

<style lang="scss" scoped>
  .c-featured-products {
    padding: $section-padding;
    text-align: center;

    &__title {
      max-width: rem(600px);
      margin: auto;
      text-transform: uppercase;
    }

    &__subtitle {
      max-width: rem(385px);
      margin: auto;
    }

    &__width {
      cursor: pointer;
      border: none;
      background: none;

      &:not(:last-child) {
        margin-right: rem(50px);
      }
      @include respond(phone) {
        &:not(:last-child) {
          margin-right: rem(25px);
        }
      }
    }

    ::v-deep {
      .flickity-prev-next-button {
        align-items: center;
        justify-content: center;
        @media only screen and (min-width: 75em) {
          display: flex;
        }
      }
      .flickity-prev-next-button svg.flickity-button-icon {
        position: unset;
        transform: unset;
      }
    }
  }
.c-featured-products__carousel--button-primary {
  ::v-deep {
    button.flickity-prev-next-button {
      @include themify($themes) {
        background-color: themed('background', 'dark');
      }
    }
    .flickity-prev-next-button svg.flickity-button-icon {
      @include themify($themes) {
        color: themed('foreground', 'white-text');
      }
    }
  }
}
.c-featured-products__carousel--button-accent {
  ::v-deep {
    button.flickity-prev-next-button {
      @include themify($themes) {
        background-color: themed('background', 'accent');
      }
    }
    .flickity-prev-next-button svg.flickity-button-icon {
      @include themify($themes) {
        color: themed('foreground', 'white-text');
      }
    }
  }
}
</style>
